import registry, { StimuliHookProps } from "../hooks/hook-registry";
import { SocialMediaDriver } from "../interaction_tracking/network";
import { AssetFile } from "../manifest/models/asset.models";
import { Author } from "../manifest/models/author.models";
import { ExperimentBranding } from "../manifest/models/branding.models";
import { CommentBank } from "../manifest/models/comment.models";
import { ExperimentCondition } from "../manifest/models/condition.models";
import { GoalFeedback } from "../manifest/models/goal.feedback";
import { ExperimentManifest } from "../manifest/models/manifest.models";
import { ManifestParser } from "../manifest/parser";
import {
  BasicImagePost,
  BuzzfeedQuizPost,
  PaginatedArticlePost,
  PostData,
  SponsoredImagePost,
  YoutubeVideoPost,
} from "../posts/post.models";
import { FeedGenerator } from "./feed.generator";
import {
  CommentGenerationStrategy,
  DateGenerationStrategy,
  FeedGenerationStrategy,
  InteractionGenerationStrategy,
} from "./generation.models";
import { MultiStageGenerator } from "./multi_stage.generator";
import { StimuliShuffler } from "./shuffler";
import {
  SidebarContent,
  SidebarGenerator,
  SidebarStimuli,
} from "./sidebar.generator";

export interface StimuliBank {
  authors: Author[];
  comments: CommentBank;
  avatars: AssetFile[];
  goal_feedback?: GoalFeedback;
  quizzes?: BuzzfeedQuizPost[];
  videos?: YoutubeVideoPost[];
  filler?: PostData[];
  friendPosts?: PostData[];
  sidebar?: SidebarStimuli;
  branding?: ExperimentBranding;

  articles: PaginatedArticlePost[];
  advertisements: SponsoredImagePost[];
  infographics: BasicImagePost[];
  neutralInfographics: BasicImagePost[];
  memes: BasicImagePost[];

  name: string;
  condition?: ExperimentCondition;
  instructions: string;
  contact: string;
  generationStrategy: FeedGenerationStrategy;
  mobileSupported: boolean;

  commentStrategy: CommentGenerationStrategy;
  dateStrategy: DateGenerationStrategy;
  likeStrategy: InteractionGenerationStrategy;
  dislikeStrategy: InteractionGenerationStrategy;
  shareStrategy: InteractionGenerationStrategy;
  metadata?: { [key: string]: any };
  user: Author;
}

export class MissingManifestError extends Error {}

export class StimuliController {
  private manifest!: ExperimentManifest;
  private condition!: string;
  private driver!: SocialMediaDriver;
  private bank!: StimuliBank;
  private _posts!: PostData[];
  private _sidebar!: SidebarContent;
  stage?: number;

  constructor(driver: SocialMediaDriver) {
    this.driver = driver;
  }

  async load(
    subjectId: string,
    conditionName: string,
    disableMultistage: boolean = false,
  ) {
    this.condition = conditionName;
    this.manifest = await this.fetchManifest();

    const parser = new ManifestParser();
    this.bank = await parser.parse(this.manifest, this.condition);

    var hookProps: StimuliHookProps = {
      subjectId: subjectId,
      conditionName: conditionName,
      disableMultistage: disableMultistage,
      bank: this.bank,
    };

    const metadata = registry.generateMetadata(hookProps);
    this.bank.metadata = metadata;

    const shuffler = new StimuliShuffler();
    this.bank = shuffler.randomizeStimuli(this.bank);

    if (!disableMultistage) {
      const multiStageGenerator = new MultiStageGenerator(
        this.driver,
        this.bank,
        subjectId,
      );
      console.debug("quizzes prior to multi-stage", this.bank.quizzes);
      this.bank = await multiStageGenerator.overwriteStimuli();

      console.debug("quizzes after multi-stage", this.bank.quizzes);
      this.stage = multiStageGenerator.lookupCurrentStage();
    }

    hookProps = {
      subjectId: subjectId,
      conditionName: conditionName,
      disableMultistage: disableMultistage,
      bank: this.bank,
    };

    const feedGenerator = new FeedGenerator(this.bank, this.stage);
    this._posts = await feedGenerator.generate();
    this._posts = registry.generatePosts(hookProps, this._posts);

    const sidebarGenerator = new SidebarGenerator();
    this._sidebar = sidebarGenerator.generate(this.bank);
    this._sidebar = registry.generateSidebar(hookProps, this._sidebar);
  }

  get stimuli(): StimuliBank {
    return this.bank;
  }

  get posts(): PostData[] {
    return this._posts;
  }

  get sidebar(): SidebarContent {
    return this._sidebar;
  }

  async fetchManifest(): Promise<ExperimentManifest> {
    const response = await fetch("./experiment_manifest.json");
    if (!response.ok) {
      throw new MissingManifestError();
    }

    return await response.json();
  }
}
